import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { FileDown, Percent, Store, Users, Check, X, Ban, Wallet, CalendarDays, ClipboardList, IdCard } from "lucide-react";
import { format as formatDate, parseISO as parseIsoDate } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import SearchableSelect from "./SearchableSelect";
import useBeneficiaryCategoryMap from "../hooks/useBeneficiaryCategoryMap";
import useIsMobile from "../hooks/useIsMobile";
import { toast } from "sonner";
import CommissionBeneficiariesPanel from "./commissions/CommissionBeneficiariesPanel";

const money = (v) =>
  `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SOURCES = [
  { key: "", label: "Toutes sources", icon: null },
  { key: "site", label: "Site (Agence/PDV)", icon: <Store size={12} /> },
  { key: "partner", label: "Partenaire (code)", icon: <Users size={12} /> },
];

/**
 * Onglet « Commissions partenaires » — tableau des commissions générées
 * automatiquement à partir des transactions (site type Agence/PDV → 30 €,
 * code promo/affiliate → commission par service). Refund automatique = annulation.
 *
 * Props :
 *   - brands, allSites, all partners (bénéficiaires), allServices : contexts partagés
 *     par la page KPI (évite les fetch en double).
 */
export default function CommissionsTab({ allSites = [], allServices = [], beneficiaries = [], defaultSubTab = "commissions" }) {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const isMobile = useIsMobile();
  // Le Gestionnaire consulte les commissions et peut télécharger les PDF,
  // mais ne peut ni modifier (édition inline du montant) ni annuler manuellement.
  const canEdit = user?.role === "admin" || user?.role === "manager";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    brand_id: selectedBrandId || "",
    site_id: "",
    partner_id: "",
    source: "",
    date_from: "",
    date_to: "",
    include_cancelled: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfConfig, setPdfConfig] = useState({
    scope: "global", // global | site | partner
    site_id: "",
    partner_id: "",
    date_from: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      const res = await api.get("/commissions", { params: { ...params, limit: 2000 } });
      setItems(res.data || []);
    } catch (e) {
      toast.error("Impossible de charger les commissions");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Resynchronise le filtre Département avec le sélecteur global du header
  useEffect(() => {
    setFilters((f) => (f.brand_id === (selectedBrandId || "") ? f : { ...f, brand_id: selectedBrandId || "", site_id: "" }));
  }, [selectedBrandId]);

  // Reset partner/site scoping in PDF form when scope changes
  useEffect(() => {
    if (pdfConfig.scope !== "site") setPdfConfig((c) => ({ ...c, site_id: "" }));
    if (pdfConfig.scope !== "partner") setPdfConfig((c) => ({ ...c, partner_id: "" }));
  }, [pdfConfig.scope]);

  const kpis = useMemo(() => {
    const active = items.filter((c) => !c.cancelled);
    const total = active.reduce((s, c) => s + Number(c.amount_ht || 0), 0);
    const sites = active.filter((c) => c.source === "site").reduce((s, c) => s + Number(c.amount_ht || 0), 0);
    const partners = active.filter((c) => c.source === "partner").reduce((s, c) => s + Number(c.amount_ht || 0), 0);
    const cancelled = items.filter((c) => c.cancelled).length;
    return { count: active.length, total, sites, partners, cancelled };
  }, [items]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditingValue(String(c.amount_ht ?? 0));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };
  const commitEdit = async (c) => {
    const parsed = parseFloat(String(editingValue).replace(",", "."));
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("Montant invalide");
      return;
    }
    try {
      const res = await api.put(`/commissions/${c.id}`, { amount_ht: parsed });
      setItems((prev) => prev.map((x) => (x.id === c.id ? res.data : x)));
      cancelEdit();
      toast.success("Commission mise à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    }
  };

  const downloadPdf = async () => {
    if (pdfConfig.scope === "site" && !pdfConfig.site_id) {
      toast.error("Sélectionnez un site");
      return;
    }
    if (pdfConfig.scope === "partner" && !pdfConfig.partner_id) {
      toast.error("Sélectionnez un partenaire");
      return;
    }
    try {
      const params = {
        scope: pdfConfig.scope,
        date_from: pdfConfig.date_from,
        date_to: pdfConfig.date_to,
      };
      if (pdfConfig.scope === "site") params.site_id = pdfConfig.site_id;
      if (pdfConfig.scope === "partner") params.partner_id = pdfConfig.partner_id;
      if (filters.brand_id) params.brand_id = filters.brand_id;
      const res = await api.get("/commissions/summary/pdf", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const bits = [pdfConfig.scope];
      if (pdfConfig.scope === "site") {
        const s = allSites.find((x) => x.id === pdfConfig.site_id);
        if (s) bits.push(s.name.replace(/[^a-z0-9]/gi, "_"));
      }
      if (pdfConfig.scope === "partner") {
        const p = beneficiaries.find((x) => x.id === pdfConfig.partner_id);
        if (p) bits.push((p.denomination || "partenaire").replace(/[^a-z0-9]/gi, "_"));
      }
      bits.push(pdfConfig.date_from);
      bits.push(pdfConfig.date_to);
      a.download = `commissions_${bits.join("_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
      setPdfOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'export PDF");
    }
  };

  const { getIdByName: getCatIdByName, categories: _catList } = useBeneficiaryCategoryMap();
  const partnersOnly = useMemo(() => {
    const partnerCatId = getCatIdByName("Partenaire");
    // Filtre strict : dès qu'au moins une catégorie est connue et que
    // « Partenaire » n'est pas trouvée, on renvoie liste vide plutôt que
    // tout afficher (cohérence avec BeneficiaryCombobox).
    if (!partnerCatId) return _catList.length > 0 ? [] : beneficiaries;
    return beneficiaries.filter((b) => b.category_id === partnerCatId);
  }, [beneficiaries, getCatIdByName, _catList.length]);
  const brandOptions = useMemo(() => brands || [], [brands]);
  const filteredSites = useMemo(() => {
    if (!filters.brand_id) return allSites;
    return allSites.filter((s) => (s.brand_ids || []).includes(filters.brand_id));
  }, [allSites, filters.brand_id]);

  return (
    <div className="space-y-4" data-testid="commissions-tab">
      <Tabs defaultValue={defaultSubTab === "beneficiaires" ? "beneficiaires" : "commissions"} className="w-full">
        <TabsList className="bg-zinc-100 border border-zinc-200" data-testid="commissions-subtabs">
          <TabsTrigger value="commissions" data-testid="commissions-subtab-commissions">
            <ClipboardList size={12} className="mr-1.5" />
            Commissions partenaires affiliés
          </TabsTrigger>
          <TabsTrigger value="beneficiaires" data-testid="commissions-subtab-beneficiaires">
            <IdCard size={12} className="mr-1.5" />
            Bénéficiaires affiliation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="commissions" className="space-y-4 mt-4">
      {/* KPIs header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total actif" value={money(kpis.total)} accent="#0EA5E9" icon={<Wallet size={14} />} tid="kpi-total" />
        <KpiCard label="Sites (Agence/PDV)" value={money(kpis.sites)} accent="#0EA5E9" icon={<Store size={14} />} tid="kpi-sites" />
        <KpiCard label="Partenaires (codes)" value={money(kpis.partners)} accent="#7C3AED" icon={<Users size={14} />} tid="kpi-partners" />
        <KpiCard label="Lignes actives" value={kpis.count} icon={<Check size={14} />} tid="kpi-count" />
        <KpiCard label="Lignes annulées" value={kpis.cancelled} accent="#DC2626" icon={<Ban size={14} />} tid="kpi-cancelled" />
      </div>

      {/* Filters + PDF button */}
      <div className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-wrap items-end gap-3">
        <FilterField label="Département">
          <Select value={filters.brand_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, brand_id: v === "all" ? "" : v, site_id: "" }))}>
            <SelectTrigger className="h-8 text-xs w-[170px]" data-testid="filter-brand"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {brandOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Site">
          <Select value={filters.site_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, site_id: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs w-[170px]" data-testid="filter-site"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {filteredSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Partenaire">
          <SearchableSelect
            value={filters.partner_id || "all"}
            onChange={(v) => setFilters((f) => ({ ...f, partner_id: v === "all" ? "" : v }))}
            options={[{ value: "all", label: "Tous" }, ...partnersOnly.map((b) => ({ value: b.id, label: b.denomination, keywords: b.email || "" }))]}
            placeholder="Tous"
            searchPlaceholder="Rechercher un partenaire…"
            className="h-8 text-xs w-[170px]"
            data-testid="filter-partner"
          />
        </FilterField>
        <FilterField label="Source">
          <Select value={filters.source || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, source: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs w-[170px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => <SelectItem key={s.key || "all"} value={s.key || "all"}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Période">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-8 rounded-sm font-normal text-xs justify-start px-2 min-w-[180px] max-w-[260px]"
                data-testid="filter-date-range-btn"
              >
                <CalendarDays size={13} className="mr-1.5 text-zinc-500 flex-shrink-0" />
                <span className="truncate">
                  {filters.date_from && filters.date_to
                    ? `${formatDate(parseIsoDate(filters.date_from), "dd MMM yyyy", { locale: frLocale })} → ${formatDate(parseIsoDate(filters.date_to), "dd MMM yyyy", { locale: frLocale })}`
                    : filters.date_from
                      ? `Depuis ${formatDate(parseIsoDate(filters.date_from), "dd MMM yyyy", { locale: frLocale })}`
                      : filters.date_to
                        ? `Jusqu'au ${formatDate(parseIsoDate(filters.date_to), "dd MMM yyyy", { locale: frLocale })}`
                        : "Toute la période"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto max-w-[95vw] overflow-auto" align="start" data-testid="filter-date-range-popover">
              <Calendar
                mode="range"
                numberOfMonths={isMobile ? 1 : 2}
                locale={frLocale}
                selected={{
                  from: filters.date_from ? parseIsoDate(filters.date_from) : undefined,
                  to: filters.date_to ? parseIsoDate(filters.date_to) : undefined,
                }}
                onSelect={(r) => {
                  if (!r) { setFilters((f) => ({ ...f, date_from: "", date_to: "" })); return; }
                  setFilters((f) => ({
                    ...f,
                    date_from: r.from ? formatDate(r.from, "yyyy-MM-dd") : "",
                    date_to: r.to ? formatDate(r.to, "yyyy-MM-dd") : (r.from ? formatDate(r.from, "yyyy-MM-dd") : ""),
                  }));
                }}
                initialFocus
                data-testid="filter-date-range-calendar"
              />
              <div className="flex items-center justify-between gap-2 p-2 border-t border-zinc-200 bg-zinc-50">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilters((f) => ({ ...f, date_from: "", date_to: "" }))}
                  data-testid="filter-date-range-reset"
                >
                  Réinitialiser
                </Button>
                <span className="text-[10px] font-mono text-zinc-500">
                  {filters.date_from && filters.date_to ? "Plage sélectionnée" : "Sélectionnez une plage"}
                </span>
              </div>
            </PopoverContent>
          </Popover>
        </FilterField>
        <label className="inline-flex items-center gap-1.5 text-xs text-zinc-600 font-mono select-none">
          <input
            type="checkbox"
            checked={filters.include_cancelled}
            onChange={(e) => setFilters((f) => ({ ...f, include_cancelled: e.target.checked }))}
            data-testid="filter-include-cancelled"
          />
          Inclure les annulées
        </label>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-zinc-300 rounded-sm text-xs"
            onClick={() => setPdfOpen(true)}
            data-testid="open-pdf-modal"
          >
            <FileDown size={13} className="mr-1" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Site / Partenaire</th>
              <th className="text-left px-3 py-2">Service / Produit</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-right px-3 py-2">Commission HT</th>
              <th className="text-left px-3 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-400 font-mono text-xs">Chargement…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-500 font-mono text-xs">Aucune commission sur la période / le filtre sélectionné.</td></tr>
            )}
            {items.map((c) => {
              const who = c.source === "site" ? c.site_name : c.partner_name;
              const whoIcon = c.source === "site" ? <Store size={11} /> : <Users size={11} />;
              const isEditing = editingId === c.id;
              return (
                <tr
                  key={c.id}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 ${c.cancelled ? "opacity-60" : ""}`}
                  data-testid={`commission-row-${c.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{c.transaction_date || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      {whoIcon} {who || <span className="text-zinc-400">—</span>}
                    </span>
                    {c.brand_name && (
                      <span className="ml-1 text-[10px] font-mono text-zinc-400">· {c.brand_name}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{c.service_product_name || "—"}</td>
                  <td className="px-3 py-2">
                    {c.source === "site" ? (
                      <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50 rounded-sm text-[10px] font-mono">Site</Badge>
                    ) : (
                      <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-50 rounded-sm text-[10px] font-mono">
                        Partenaire {c.code_value ? `· ${c.code_value}` : ""}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEditing ? (
                      <div className="inline-flex items-center gap-1 justify-end">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitEdit(c);
                            if (e.key === "Escape") cancelEdit();
                          }}
                          className="h-7 w-24 text-xs text-right font-mono"
                          autoFocus
                          data-testid={`commission-edit-input-${c.id}`}
                        />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => commitEdit(c)} data-testid={`commission-save-${c.id}`}><Check size={13} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-500" onClick={cancelEdit} data-testid={`commission-cancel-${c.id}`}><X size={13} /></Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={c.cancelled || c.is_refund_mirror || !canEdit}
                        onClick={() => { if (canEdit && !c.is_refund_mirror) startEdit(c); }}
                        className={`inline-flex items-center gap-1 font-mono font-semibold px-1.5 py-0.5 rounded-sm ${(c.cancelled || c.is_refund_mirror || !canEdit) ? `cursor-default ${c.is_refund_mirror ? "text-red-700" : "text-zinc-700"}` : "hover:bg-orange-50 hover:text-orange-700 text-zinc-900"}`}
                        data-testid={`commission-amount-${c.id}`}
                        title={c.is_refund_mirror ? "Commission miroir de remboursement (non éditable)" : canEdit ? "Modifier le montant" : "Consultation seule"}
                      >
                        {money(c.amount_ht)}
                        {c.manually_edited && <Percent size={10} className="text-orange-500" title="Modifié manuellement" />}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {c.is_refund_mirror ? (
                      <Badge className="bg-red-100 text-red-700 rounded-sm text-[10px] font-mono" data-testid={`commission-refund-badge-${c.id}`}>Remboursement</Badge>
                    ) : c.cancelled ? (
                      <Badge className="bg-red-100 text-red-700 rounded-sm text-[10px] font-mono">Annulée</Badge>
                    ) : c.manually_edited ? (
                      <Badge variant="outline" className="border-orange-300 text-orange-700 bg-orange-50 rounded-sm text-[10px] font-mono">Modifiée</Badge>
                    ) : (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 rounded-sm text-[10px] font-mono">Auto</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PDF Export modal */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="pdf-modal">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">Export PDF — Commissions</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Génère une synthèse détaillée : ventes, CA, services, commission unitaire et total sur la période choisie.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-zinc-500">Portée</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { key: "global", label: "Global" },
                  { key: "site", label: "Par site" },
                  { key: "partner", label: "Par partenaire" },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setPdfConfig((c) => ({ ...c, scope: s.key }))}
                    className={`h-9 rounded-sm border text-xs font-mono uppercase tracking-wider ${pdfConfig.scope === s.key ? "bg-[#F97316] border-[#F97316] text-white" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}
                    data-testid={`pdf-scope-${s.key}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>

            {pdfConfig.scope === "site" && (
              <div>
                <Label className="text-xs uppercase font-mono text-zinc-500">Site</Label>
                <Select value={pdfConfig.site_id || "none"} onValueChange={(v) => setPdfConfig((c) => ({ ...c, site_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="pdf-site"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sélectionner —</SelectItem>
                    {allSites.filter((s) => s.type === "agence" || s.type === "pdv").map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {pdfConfig.scope === "partner" && (
              <div>
                <Label className="text-xs uppercase font-mono text-zinc-500">Partenaire</Label>
                <SearchableSelect
                  value={pdfConfig.partner_id}
                  onChange={(v) => setPdfConfig((c) => ({ ...c, partner_id: v }))}
                  options={partnersOnly.map((b) => ({ value: b.id, label: b.denomination, keywords: b.email || "" }))}
                  placeholder="Sélectionner un partenaire"
                  searchPlaceholder="Rechercher un partenaire…"
                  emptyLabel="Aucun contact « Partenaire » disponible"
                  className="h-8 text-xs"
                  data-testid="pdf-partner"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-mono text-zinc-500">Du</Label>
                <Input type="date" className="h-8 text-xs" value={pdfConfig.date_from} onChange={(e) => setPdfConfig((c) => ({ ...c, date_from: e.target.value }))} data-testid="pdf-date-from" />
              </div>
              <div>
                <Label className="text-xs uppercase font-mono text-zinc-500">Au</Label>
                <Input type="date" className="h-8 text-xs" value={pdfConfig.date_to} onChange={(e) => setPdfConfig((c) => ({ ...c, date_to: e.target.value }))} data-testid="pdf-date-to" />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {[
                { key: "day", label: "Aujourd'hui", days: 0 },
                { key: "week", label: "Semaine", days: 7 },
                { key: "month", label: "Mois", days: 30 },
                { key: "year", label: "Année", days: 365 },
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    const to = new Date();
                    const from = new Date();
                    from.setDate(to.getDate() - p.days);
                    setPdfConfig((c) => ({ ...c, date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) }));
                  }}
                  className="h-7 rounded-sm border border-zinc-300 text-[10px] font-mono uppercase tracking-wider hover:bg-zinc-50 text-zinc-700"
                  data-testid={`pdf-preset-${p.key}`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOpen(false)} className="rounded-sm text-xs" data-testid="pdf-cancel">Annuler</Button>
            <Button onClick={downloadPdf} className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm text-xs" data-testid="pdf-download">
              <FileDown size={13} className="mr-1" /> Télécharger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="beneficiaires" className="space-y-4 mt-4">
          <CommissionBeneficiariesPanel isAdmin={user?.role === "admin"} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ label, value, accent, icon, tid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-3 flex items-center gap-3" data-testid={tid}>
      <div
        className="h-8 w-8 rounded-sm inline-flex items-center justify-center text-white shrink-0"
        style={{ backgroundColor: accent || "#1F2937" }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 truncate">{label}</div>
        <div className="text-lg font-semibold font-mono text-zinc-900 truncate">{value}</div>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">{label}</span>
      {children}
    </div>
  );
}
